import React, { useState, useEffect } from 'react';
import './NovaObraButton.css';
import { apiService } from '../../services/ApiService';
import { useAuth } from '../../contexts/AuthContext';

const NovaObraButton = ({ onSave }) => {
  const { currentUser } = useAuth();
  // Função para obter a data atual formatada como YYYY-MM-DD
  const obterDataAtual = () => {
    const hoje = new Date();
    const ano = hoje.getFullYear();
    const mes = String(hoje.getMonth() + 1).padStart(2, '0');
    const dia = String(hoje.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  };

  const [modalAberto, setModalAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);
  const [novaObra, setNovaObra] = useState({
    nome: '',
    endereco: '',
    cliente: '',
    dataInicio: obterDataAtual(),
    responsavel: '',
    previsaoConclusao: ''
  });
  
  // Estados para a busca de clientes
  const [clientesList, setClientesList] = useState([]);
  const [clientesFiltrados, setClientesFiltrados] = useState([]);
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [mostrarListaClientes, setMostrarListaClientes] = useState(false);

  // Verificar se o usuário é um cliente
  const isClient = currentUser?.role === 'client';

  // Se for cliente, não renderizar nada
  if (isClient) {
    return null;
  }

  // Carregar lista de clientes quando o modal for aberto
  useEffect(() => {
    if (modalAberto) {
      carregarClientes();
    }
  }, [modalAberto]);

  // Função para carregar clientes da API
  const carregarClientes = async () => {
    try {
      setBuscandoCliente(true);
      const result = await apiService.getClientes();
      if (result.success && result.data && Array.isArray(result.data.clientes)) {
        setClientesList(result.data.clientes);
      } else {
        console.error('Erro ao carregar clientes:', result.error || 'Erro desconhecido');
        setClientesList([]);
      }
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      setClientesList([]);
    } finally {
      setBuscandoCliente(false);
    }
  };

  // Função para remover acentos
  const removeAcentos = (texto) => {
    if (!texto) return '';
    
    // Substituir acentos por caracteres sem acento
    return texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  };

  // Função para buscar cliente enquanto o usuário digita
  const buscarCliente = (nome) => {
    if (!nome || nome.trim().length < 2 || !clientesList.length) {
      setClientesFiltrados([]);
      setMostrarListaClientes(false);
      return;
    }
    
    setBuscandoCliente(true);
    
    // Remover acentos e converter para minúsculas para a busca
    const normalizedNome = removeAcentos(nome.trim().toLowerCase());
    
    const filtrados = clientesList.filter(cliente => 
      removeAcentos(cliente.nome?.toLowerCase() || '').includes(normalizedNome)
    );
    
    setClientesFiltrados(filtrados);
    setMostrarListaClientes(filtrados.length > 0);
    setBuscandoCliente(false);
  };

  // Função para selecionar um cliente da lista
  const selecionarCliente = (cliente) => {
    setNovaObra(prev => ({
      ...prev,
      cliente: cliente.nome,
      endereco: cliente.endereco || prev.endereco
    }));
    
    setMostrarListaClientes(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNovaObra({
      ...novaObra,
      [name]: value
    });
    
    // Se o campo for "cliente", buscar clientes enquanto digita
    if (name === 'cliente' && value.length >= 2) {
      buscarCliente(value);
    } else if (name === 'cliente') {
      setClientesFiltrados([]);
      setMostrarListaClientes(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSalvando(true);
    setErro(null);
    
    try {
      // Criar a obra usando o apiService
      const resultado = await apiService.createDiarioObra(novaObra);
      
      if (!resultado.success) {
        throw new Error(resultado.error || 'Erro ao criar obra');
      }
      
      console.log('Obra criada com sucesso:', resultado.data);
      
      // Chamar a função onSave para atualizar a lista de obras
      if (onSave && typeof onSave === 'function') {
        onSave(resultado.data.obra);
      }
    
    // Limpar formulário e fechar modal
    setNovaObra({
        nome: '',
        endereco: '',
      cliente: '',
      dataInicio: obterDataAtual(),
        responsavel: '',
        previsaoConclusao: ''
    });
    setModalAberto(false);
    } catch (error) {
      console.error('Erro ao criar a obra:', error);
      
      // Mostrar mensagem de erro mais amigável
      let mensagemErro = error.message || 'Não foi possível criar a obra. Tente novamente.';
      
      // Se for erro de endpoint não encontrado, mostrar mensagem específica
      if (mensagemErro.includes('Endpoint não encontrado') || mensagemErro.includes('404')) {
        mensagemErro = 'O serviço de criação de obra está temporariamente indisponível. Tente novamente mais tarde.';
      } else if (mensagemErro.includes('Erro de conexão')) {
        mensagemErro = 'Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.';
      }
      
      setErro(mensagemErro);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <>
      <button 
        className="nova-obra-button" 
        onClick={() => setModalAberto(true)}
        title="Adicionar nova obra"
      >
        <i className="fas fa-plus"></i>
        <span>Nova Obra</span>
      </button>

      {modalAberto && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h2>Nova Obra</h2>
              <button 
                className="btn-fechar" 
                onClick={() => setModalAberto(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {erro && (
              <div className="erro-mensagem">
                <i className="fas fa-exclamation-triangle"></i> {erro}
              </div>
            )}

            <form className="form-nova-obra" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="nome">Nome da Obra</label>
                <input
                  type="text"
                  id="nome"
                  name="nome"
                  value={novaObra.nome}
                  onChange={handleChange}
                  placeholder="Nome ou descrição da obra"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="cliente">Cliente</label>
                <div className="cliente-search-container">
                  <input
                    type="text"
                    id="cliente"
                    name="cliente"
                    value={novaObra.cliente}
                    onChange={handleChange}
                    placeholder="Nome do cliente"
                    required
                    autoComplete="off"
                  />
                  {buscandoCliente && <div className="cliente-buscando">Buscando cliente...</div>}
                  
                  {mostrarListaClientes && (
                    <div className="clientes-dropdown">
                      {clientesFiltrados.map((cliente, index) => (
                        <div 
                          key={cliente.id || index} 
                          className="cliente-option" 
                          onClick={() => selecionarCliente(cliente)}
                        >
                          {cliente.nome}
                          {cliente.endereco && <span className="cliente-endereco">{cliente.endereco}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="endereco">Endereço</label>
                <input
                  type="text"
                  id="endereco"
                  name="endereco"
                  value={novaObra.endereco}
                  onChange={handleChange}
                  placeholder="Endereço completo da obra"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="responsavel">Engenheiro Responsável</label>
                <input
                  type="text"
                  id="responsavel"
                  name="responsavel"
                  value={novaObra.responsavel}
                  onChange={handleChange}
                  placeholder="Nome do engenheiro responsável"
                />
              </div>

                <div className="form-group">
                  <label htmlFor="dataInicio">Data de Início</label>
                  <input
                    type="date"
                    id="dataInicio"
                    name="dataInicio"
                    value={novaObra.dataInicio}
                    onChange={handleChange}
                    required
                  />
              </div>

              <div className="form-group">
                <label htmlFor="previsaoConclusao">Previsão de Conclusão</label>
                <input
                  type="date"
                  id="previsaoConclusao"
                  name="previsaoConclusao"
                  value={novaObra.previsaoConclusao}
                  onChange={handleChange}
                  placeholder="Data prevista para conclusão"
                />
              </div>

              <div className="diario-obra-buttons">
                <button 
                  type="button" 
                  className="btn-cancelar"
                  onClick={() => setModalAberto(false)}
                  disabled={salvando}
                >
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  className="btn-criar"
                  disabled={salvando}
                >
                  {salvando ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> Salvando...
                    </>
                  ) : 'Criar Obra'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default NovaObraButton; 